'use client';
import React, { useState, useEffect } from 'react';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';
import SessionReviewModal from '@/components/SessionReviewModal';

// ─── Types ────────────────────────────────────────────────────────────────────
type FilterTab = 'all' | 'upcoming' | 'past' | 'cancelled';

interface ConsultantBooking {
  id: string;
  consultantName: string;
  consultantInitials: string;
  consultantTitle: string;
  consultantCategory: string;
  service: string;
  date: string;
  time: string;
  duration: string;
  amountUsd: number;
  paymentStatus: 'paid' | 'pending' | 'refunded' | 'failed';
  bookingStatus: 'upcoming' | 'completed' | 'cancelled' | 'pending';
  meetingLink?: string;
  notes?: string;
  bookedAt: string;
  providerUserId: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function formatDate(dateStr: string): string {
  const d = new Date(dateStr + 'T00:00:00');
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function getPaymentBadge(status: ConsultantBooking['paymentStatus']) {
  const map: Record<string, { label: string; cls: string }> = {
    paid: { label: 'Paid', cls: 'bg-emerald-100 text-emerald-700' },
    pending: { label: 'Pending', cls: 'bg-amber-100 text-amber-700' },
    refunded: { label: 'Refunded', cls: 'bg-blue-100 text-blue-700' },
    failed: { label: 'Failed', cls: 'bg-red-100 text-red-700' },
  };
  return map[status] ?? { label: status, cls: 'bg-muted text-muted-foreground' };
}

function getStatusBadge(status: ConsultantBooking['bookingStatus']) {
  const map: Record<string, { label: string; cls: string; icon: string }> = {
    upcoming: { label: 'Upcoming', cls: 'bg-primary/10 text-primary', icon: 'ClockIcon' },
    completed: { label: 'Completed', cls: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircleIcon' },
    cancelled: { label: 'Cancelled', cls: 'bg-red-100 text-red-700', icon: 'XCircleIcon' },
    pending: { label: 'Pending Confirm', cls: 'bg-amber-100 text-amber-700', icon: 'ExclamationCircleIcon' },
  };
  return map[status] ?? { label: status, cls: 'bg-muted text-muted-foreground', icon: 'QuestionMarkCircleIcon' };
}

function getCategoryColor(category: string): string {
  const map: Record<string, string> = {
    'Islamic Finance': 'bg-teal-100 text-teal-700',
    'Business Strategy': 'bg-violet-100 text-violet-700',
    'Tech & Digital': 'bg-blue-100 text-blue-700',
    'Halal Legal': 'bg-orange-100 text-orange-700',
    'Halal Marketing': 'bg-pink-100 text-pink-700',
  };
  return map[category] ?? 'bg-muted text-muted-foreground';
}

// ─── Reschedule Modal ─────────────────────────────────────────────────────────
function RescheduleModal({
  booking,
  onClose,
  onConfirm,
}: {
  booking: ConsultantBooking;
  onClose: () => void;
  onConfirm: (id: string, date: string, time: string) => void;
}) {
  const [newDate, setNewDate] = useState('');
  const [newTime, setNewTime] = useState('');

  const timeSlots = [
    '09:00 AM', '10:00 AM', '11:00 AM', '12:00 PM',
    '01:00 PM', '02:00 PM', '03:00 PM', '04:00 PM', '05:00 PM',
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-md border border-border">
        <div className="flex items-center justify-between px-6 py-5 border-b border-border">
          <div>
            <h3 className="text-base font-600 text-foreground">Reschedule Session</h3>
            <p className="text-xs text-muted-foreground mt-0.5">{booking.service}</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted text-muted-foreground">
            <Icon name="XMarkIcon" size={18} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          <div>
            <label className="block text-xs font-500 text-muted-foreground mb-1.5">New Date</label>
            <input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2.5 rounded-xl border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>
          <div>
            <label className="block text-xs font-500 text-muted-foreground mb-1.5">New Time</label>
            <div className="grid grid-cols-3 gap-2">
              {timeSlots.map((slot) => (
                <button
                  key={slot}
                  onClick={() => setNewTime(slot)}
                  className={`px-2 py-2 rounded-lg text-xs font-500 border transition-all ${
                    newTime === slot
                      ? 'bg-primary text-primary-foreground border-primary'
                      : 'border-border text-muted-foreground hover:border-primary/50 hover:text-foreground'
                  }`}
                >
                  {slot}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-border flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => { if (newDate && newTime) onConfirm(booking.id, newDate, newTime); }}
            disabled={!newDate || !newTime}
            className="flex-1 px-4 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Confirm Reschedule
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Cancel Modal ─────────────────────────────────────────────────────────────
function CancelModal({
  booking,
  onClose,
  onConfirm,
}: {
  booking: ConsultantBooking;
  onClose: () => void;
  onConfirm: (id: string) => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm border border-border">
        <div className="px-6 py-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <Icon name="ExclamationTriangleIcon" size={24} className="text-red-600" />
          </div>
          <h3 className="text-base font-600 text-foreground mb-1">Cancel Booking?</h3>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to cancel your session with{' '}
            <span className="font-500 text-foreground">{booking.consultantName}</span>?
          </p>
          {booking.paymentStatus === 'paid' && (
            <div className="mt-3 px-3 py-2 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                A refund of <span className="font-600">${booking.amountUsd}</span> will be processed within 5–7 business days.
              </p>
            </div>
          )}
        </div>
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-xl border border-border text-sm font-500 text-muted-foreground hover:bg-muted transition-colors"
          >
            Keep Booking
          </button>
          <button
            onClick={() => onConfirm(booking.id)}
            className="flex-1 px-4 py-2.5 rounded-xl bg-red-600 text-white text-sm font-600 hover:bg-red-700 transition-colors"
          >
            Yes, Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Booking Card ─────────────────────────────────────────────────────────────
function BookingCard({
  booking,
  onReschedule,
  onCancel,
  onReview,
}: {
  booking: ConsultantBooking;
  onReschedule: (b: ConsultantBooking) => void;
  onCancel: (b: ConsultantBooking) => void;
  onReview: (b: ConsultantBooking) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const statusBadge = getStatusBadge(booking.bookingStatus);
  const paymentBadge = getPaymentBadge(booking.paymentStatus);
  const categoryColor = getCategoryColor(booking.consultantCategory);
  const canAct = booking.bookingStatus === 'upcoming' || booking.bookingStatus === 'pending';

  function handleDownloadReceipt() {
    // Build a simple printable receipt as an HTML blob and trigger download
    const receiptHtml = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8" />
<title>Payment Receipt – ${booking.id.slice(0, 8).toUpperCase()}</title>
<style>
  body { font-family: sans-serif; max-width: 520px; margin: 40px auto; color: #111; }
  h1 { font-size: 20px; margin-bottom: 4px; }
  .sub { color: #666; font-size: 13px; margin-bottom: 24px; }
  table { width: 100%; border-collapse: collapse; font-size: 14px; }
  td { padding: 8px 0; border-bottom: 1px solid #eee; }
  td:last-child { text-align: right; font-weight: 600; }
  .total td { font-size: 16px; font-weight: 700; border-bottom: none; padding-top: 16px; }
  .badge { display: inline-block; padding: 2px 10px; border-radius: 999px; font-size: 12px; background: #d1fae5; color: #065f46; }
  footer { margin-top: 32px; font-size: 12px; color: #999; text-align: center; }
</style>
</head>
<body>
<h1>Payment Receipt</h1>
<p class="sub">Receipt #${booking.id.slice(0, 8).toUpperCase()} &nbsp;·&nbsp; <span class="badge">Paid</span></p>
<table>
  <tr><td>Consultant</td><td>${booking.consultantName}</td></tr>
  <tr><td>Title</td><td>${booking.consultantTitle}</td></tr>
  <tr><td>Service</td><td>${booking.service}</td></tr>
  <tr><td>Date</td><td>${formatDate(booking.date)}</td></tr>
  <tr><td>Time</td><td>${booking.time}</td></tr>
  <tr><td>Duration</td><td>${booking.duration}</td></tr>
  <tr><td>Payment Status</td><td>${booking.paymentStatus.charAt(0).toUpperCase() + booking.paymentStatus.slice(1)}</td></tr>
  <tr class="total"><td>Amount Paid</td><td>$${booking.amountUsd.toFixed(2)}</td></tr>
</table>
<footer>Booked on ${formatDate(booking.bookedAt)} &nbsp;·&nbsp; Rafeeq Consultation Platform</footer>
</body>
</html>`;

    const blob = new Blob([receiptHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `receipt-${booking.id.slice(0, 8).toUpperCase()}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden hover:shadow-card transition-shadow duration-200">
      <div className="px-5 py-4 flex items-start gap-4">
        <div className="w-11 h-11 rounded-xl gradient-teal flex items-center justify-center text-white font-600 text-sm flex-shrink-0">
          {booking.consultantInitials}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 flex-wrap">
            <div>
              <p className="text-sm font-600 text-foreground">{booking.consultantName}</p>
              <p className="text-xs text-muted-foreground truncate">{booking.consultantTitle}</p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-500 ${statusBadge.cls}`}>
                <Icon name={statusBadge.icon as Parameters<typeof Icon>[0]['name']} size={12} />
                {statusBadge.label}
              </span>
            </div>
          </div>
          <div className="mt-2 flex flex-wrap gap-2">
            <span className={`px-2 py-0.5 rounded-full text-xs font-500 ${categoryColor}`}>
              {booking.consultantCategory}
            </span>
            <span className={`px-2 py-0.5 rounded-full text-xs font-500 ${paymentBadge.cls}`}>
              {paymentBadge.label}
            </span>
          </div>
        </div>
      </div>

      <div className="px-5 pb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div className="flex items-center gap-2">
          <Icon name="CalendarDaysIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Date</p>
            <p className="text-xs font-500 text-foreground">{formatDate(booking.date)}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="ClockIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Time</p>
            <p className="text-xs font-500 text-foreground">{booking.time}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="BriefcaseIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Duration</p>
            <p className="text-xs font-500 text-foreground">{booking.duration}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Icon name="CurrencyDollarIcon" size={14} className="text-muted-foreground flex-shrink-0" />
          <div>
            <p className="text-xs text-muted-foreground">Amount</p>
            <p className="text-xs font-600 text-foreground">${booking.amountUsd}</p>
          </div>
        </div>
      </div>

      <div className="px-5 pb-3">
        <p className="text-xs text-muted-foreground">
          <span className="font-500 text-foreground">Service: </span>
          {booking.service}
        </p>
      </div>

      {booking.notes && (
        <div className="px-5 pb-3">
          <button
            onClick={() => setExpanded(!expanded)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <Icon name={expanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={12} />
            {expanded ? 'Hide notes' : 'View notes'}
          </button>
          {expanded && (
            <p className="mt-2 text-xs text-muted-foreground bg-muted/50 rounded-lg px-3 py-2 leading-relaxed">
              {booking.notes}
            </p>
          )}
        </div>
      )}

      <div className="px-5 py-3 border-t border-border flex items-center gap-2 flex-wrap">
        {booking.meetingLink && booking.bookingStatus !== 'cancelled' && (
          <a
            href={booking.meetingLink}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-500 hover:bg-primary/90 transition-colors"
          >
            <Icon name="VideoCameraIcon" size={13} />
            {booking.bookingStatus === 'upcoming' || booking.bookingStatus === 'pending' ? 'Join Session' : 'View Recording'}
          </a>
        )}
        {booking.paymentStatus === 'paid' && (
          <button
            onClick={handleDownloadReceipt}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Icon name="ArrowDownTrayIcon" size={13} />
            Receipt
          </button>
        )}
        {booking.bookingStatus === 'completed' && (
          <button
            onClick={() => onReview(booking)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-amber-300 text-xs font-500 text-amber-700 hover:bg-amber-50 transition-colors"
          >
            <Icon name="StarIcon" size={13} />
            Leave Review
          </button>
        )}
        {canAct && (
          <button
            onClick={() => onReschedule(booking)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-500 text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <Icon name="ArrowPathIcon" size={13} />
            Reschedule
          </button>
        )}
        {canAct && (
          <button
            onClick={() => onCancel(booking)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-xs font-500 text-red-600 hover:bg-red-50 transition-colors"
          >
            <Icon name="XMarkIcon" size={13} />
            Cancel
          </button>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          Booked {formatDate(booking.bookedAt)}
        </span>
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function MyBookingsPage() {
  const supabase = createClient();
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [bookings, setBookings] = useState<ConsultantBooking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rescheduleTarget, setRescheduleTarget] = useState<ConsultantBooking | null>(null);
  const [cancelTarget, setCancelTarget] = useState<ConsultantBooking | null>(null);
  const [reviewTarget, setReviewTarget] = useState<ConsultantBooking | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => {
    async function fetchBookings() {
      setLoading(true);
      setError(null);
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          setBookings([]);
          setLoading(false);
          return;
        }
        setCurrentUserId(user.id);

        const { data, error: fetchError } = await supabase
          .from('consultation_bookings')
          .select(`
            id,
            service_id,
            service_title,
            provider_name,
            booking_date,
            start_time,
            end_time,
            duration_minutes,
            status,
            payment_status,
            amount_usd,
            meeting_link,
            notes,
            created_at,
            consultant_id,
            provider_user_id
          `)
          .eq('client_user_id', user.id)
          .order('booking_date', { ascending: false });

        if (fetchError) {
          setError(fetchError.message);
          setLoading(false);
          return;
        }

        // Fetch consultant profile details for each booking
        const consultantIds = [...new Set((data ?? []).map(b => b.consultant_id).filter(Boolean))];
        let profileMap: Record<string, { display_name: string; professional_title: string; category: string }> = {};

        if (consultantIds.length > 0) {
          const { data: profiles } = await supabase
            .from('consultant_profiles')
            .select('id, display_name, professional_title, category')
            .in('id', consultantIds);

          (profiles ?? []).forEach(p => {
            profileMap[p.id] = {
              display_name: p.display_name,
              professional_title: p.professional_title,
              category: p.category,
            };
          });
        }

        const mapped: ConsultantBooking[] = (data ?? []).map((row) => {
          const profile = profileMap[row.consultant_id ?? ''];
          const consultantName = profile?.display_name ?? row.provider_name ?? 'Unknown Consultant';
          const nameWords = consultantName.split(' ');
          const initials = nameWords.slice(0, 2).map((w: string) => w[0] ?? '').join('').toUpperCase();

          // Map DB status to UI status
          const statusMap: Record<string, ConsultantBooking['bookingStatus']> = {
            pending: 'pending',
            confirmed: 'upcoming',
            completed: 'completed',
            cancelled: 'cancelled',
          };
          const bookingStatus = statusMap[row.status] ?? 'pending';

          // Map payment status
          const paymentStatusMap: Record<string, ConsultantBooking['paymentStatus']> = {
            paid: 'paid',
            unpaid: 'pending',
            refunded: 'refunded',
            failed: 'failed',
          };
          const paymentStatus = paymentStatusMap[row.payment_status ?? 'unpaid'] ?? 'pending';

          // Format time
          const startTime = row.start_time ? row.start_time.slice(0, 5) : '';
          const [h] = startTime.split(':').map(Number);
          const period = h < 12 ? 'AM' : 'PM';
          const displayTime = startTime ? `${startTime} ${period}` : '';

          // Duration label
          const mins = row.duration_minutes ?? 60;
          const durationLabel = mins < 60 ? `${mins} min` : mins === 60 ? '1 hour' : `${mins / 60} hours`;

          return {
            id: row.id,
            consultantName,
            consultantInitials: initials,
            consultantTitle: profile?.professional_title ?? '',
            consultantCategory: profile?.category ?? 'Consulting',
            service: row.service_title ?? '',
            date: row.booking_date ?? '',
            time: displayTime,
            duration: durationLabel,
            amountUsd: parseFloat(row.amount_usd ?? 0),
            paymentStatus,
            bookingStatus,
            meetingLink: row.meeting_link ?? undefined,
            notes: row.notes ?? undefined,
            bookedAt: row.created_at ? row.created_at.split('T')[0] : '',
            providerUserId: row.provider_user_id ?? '',
          };
        });

        setBookings(mapped);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Failed to load bookings');
      } finally {
        setLoading(false);
      }
    }

    fetchBookings();
  }, []);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  async function handleRescheduleConfirm(id: string, date: string, time: string) {
    const { error: updateError } = await supabase
      .from('consultation_bookings')
      .update({ booking_date: date, start_time: time })
      .eq('id', id);

    if (updateError) {
      showToast(updateError.message, 'error');
    } else {
      setBookings((prev) =>
        prev.map((b) => b.id === id ? { ...b, date, time, bookingStatus: 'upcoming' } : b)
      );
      showToast('Session rescheduled successfully.');
    }
    setRescheduleTarget(null);
  }

  async function handleCancelConfirm(id: string) {
    const { error: updateError } = await supabase
      .from('consultation_bookings')
      .update({ status: 'cancelled' })
      .eq('id', id);

    if (updateError) {
      showToast(updateError.message, 'error');
    } else {
      setBookings((prev) =>
        prev.map((b) =>
          b.id === id
            ? { ...b, bookingStatus: 'cancelled', paymentStatus: b.paymentStatus === 'paid' ? 'refunded' : b.paymentStatus }
            : b
        )
      );
      showToast('Booking cancelled. Refund initiated if applicable.');
    }
    setCancelTarget(null);
  }

  const filtered = bookings.filter((b) => {
    if (activeTab === 'all') return true;
    if (activeTab === 'upcoming') return b.bookingStatus === 'upcoming' || b.bookingStatus === 'pending';
    if (activeTab === 'past') return b.bookingStatus === 'completed';
    if (activeTab === 'cancelled') return b.bookingStatus === 'cancelled';
    return true;
  });

  const tabs: { id: FilterTab; label: string; icon: string; count: number }[] = [
    { id: 'all', label: 'All', icon: 'Squares2X2Icon', count: bookings.length },
    { id: 'upcoming', label: 'Upcoming', icon: 'CalendarDaysIcon', count: bookings.filter((b) => b.bookingStatus === 'upcoming' || b.bookingStatus === 'pending').length },
    { id: 'past', label: 'Completed', icon: 'CheckCircleIcon', count: bookings.filter((b) => b.bookingStatus === 'completed').length },
    { id: 'cancelled', label: 'Cancelled', icon: 'XCircleIcon', count: bookings.filter((b) => b.bookingStatus === 'cancelled').length },
  ];

  const totalSpent = bookings.filter((b) => b.paymentStatus === 'paid').reduce((sum, b) => sum + b.amountUsd, 0);
  const upcomingCount = bookings.filter((b) => b.bookingStatus === 'upcoming' || b.bookingStatus === 'pending').length;
  const completedCount = bookings.filter((b) => b.bookingStatus === 'completed').length;

  return (
    <AppLayout activeRoute="/my-bookings">
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-1">
              <div className="w-9 h-9 rounded-xl gradient-teal flex items-center justify-center">
                <Icon name="CalendarDaysIcon" size={18} className="text-white" />
              </div>
              <h1 className="text-2xl font-700 text-foreground">My Bookings</h1>
            </div>
            <p className="text-sm text-muted-foreground ml-12">
              Track your consultation sessions, payments, and upcoming appointments.
            </p>
          </div>

          {/* Loading */}
          {loading && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-card border border-border rounded-2xl p-5 animate-pulse">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-11 h-11 rounded-xl bg-muted flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted rounded w-1/3" />
                      <div className="h-3 bg-muted rounded w-1/2" />
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-3">
                    {[1, 2, 3, 4].map((j) => <div key={j} className="h-10 bg-muted rounded-xl" />)}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Error */}
          {!loading && error && (
            <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center">
              <Icon name="ExclamationCircleIcon" size={28} className="text-red-500 mx-auto mb-2" />
              <p className="text-sm font-600 text-red-700 mb-1">Failed to load bookings</p>
              <p className="text-xs text-red-600">{error}</p>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="bg-card border border-border rounded-2xl px-4 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="CalendarDaysIcon" size={16} className="text-primary" />
                    <p className="text-xs text-muted-foreground font-500">Upcoming</p>
                  </div>
                  <p className="text-2xl font-700 text-foreground">{upcomingCount}</p>
                  <p className="text-xs text-muted-foreground">sessions scheduled</p>
                </div>
                <div className="bg-card border border-border rounded-2xl px-4 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="CheckCircleIcon" size={16} className="text-emerald-600" />
                    <p className="text-xs text-muted-foreground font-500">Completed</p>
                  </div>
                  <p className="text-2xl font-700 text-foreground">{completedCount}</p>
                  <p className="text-xs text-muted-foreground">sessions done</p>
                </div>
                <div className="bg-card border border-border rounded-2xl px-4 py-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Icon name="CurrencyDollarIcon" size={16} className="text-amber-600" />
                    <p className="text-xs text-muted-foreground font-500">Total Spent</p>
                  </div>
                  <p className="text-2xl font-700 text-foreground">${totalSpent.toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">across all sessions</p>
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="flex gap-2 mb-6 overflow-x-auto pb-1">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-500 whitespace-nowrap transition-all ${
                      activeTab === tab.id
                        ? 'bg-primary text-primary-foreground shadow-sm'
                        : 'bg-card border border-border text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                  >
                    <Icon name={tab.icon as Parameters<typeof Icon>[0]['name']} size={14} />
                    {tab.label}
                    <span className={`ml-0.5 text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                      {tab.count}
                    </span>
                  </button>
                ))}
              </div>

              {/* Booking List */}
              {filtered.length === 0 ? (
                <div className="bg-card border border-border rounded-2xl px-6 py-16 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center mx-auto mb-4">
                    <Icon name="CalendarDaysIcon" size={28} className="text-muted-foreground" />
                  </div>
                  <p className="text-base font-600 text-foreground mb-1">No bookings found</p>
                  <p className="text-sm text-muted-foreground mb-5">
                    {activeTab === 'upcoming'
                      ? "You don't have any upcoming sessions."
                      : activeTab === 'past' ? "You haven't completed any sessions yet."
                      : activeTab === 'cancelled'? "You haven't cancelled any bookings." : "You haven't made any bookings yet."}
                  </p>
                  <a
                    href="/browse-consultants"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors"
                  >
                    <Icon name="MagnifyingGlassIcon" size={15} />
                    Browse Consultants
                  </a>
                </div>
              ) : (
                <div className="space-y-4">
                  {filtered.map((booking) => (
                    <BookingCard
                      key={booking.id}
                      booking={booking}
                      onReschedule={setRescheduleTarget}
                      onCancel={setCancelTarget}
                      onReview={setReviewTarget}
                    />
                  ))}
                </div>
              )}

              {/* CTA */}
              <div className="mt-8 bg-gradient-to-r from-primary/10 to-teal-500/10 border border-primary/20 rounded-2xl px-6 py-5 flex items-center justify-between gap-4 flex-wrap">
                <div>
                  <p className="text-sm font-600 text-foreground">Need another consultation?</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Browse verified Muslim consultants across finance, legal, tech, and more.
                  </p>
                </div>
                <a
                  href="/browse-consultants"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary text-primary-foreground text-sm font-600 hover:bg-primary/90 transition-colors flex-shrink-0"
                >
                  <Icon name="BriefcaseIcon" size={15} />
                  Find a Consultant
                </a>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      {rescheduleTarget && (
        <RescheduleModal
          booking={rescheduleTarget}
          onClose={() => setRescheduleTarget(null)}
          onConfirm={handleRescheduleConfirm}
        />
      )}
      {cancelTarget && (
        <CancelModal
          booking={cancelTarget}
          onClose={() => setCancelTarget(null)}
          onConfirm={handleCancelConfirm}
        />
      )}
      {reviewTarget && currentUserId && (
        <SessionReviewModal
          bookingId={reviewTarget.id}
          consultantName={reviewTarget.consultantName}
          consultantInitials={reviewTarget.consultantInitials}
          serviceName={reviewTarget.service}
          sessionDate={formatDate(reviewTarget.date)}
          revieweeId={reviewTarget.providerUserId}
          reviewerId={currentUserId}
          onClose={() => setReviewTarget(null)}
          onSubmitted={() => showToast('Review submitted successfully!')}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-xl shadow-lg text-sm font-500 ${toast.type === 'success' ? 'bg-emerald-600 text-white' : 'bg-red-600 text-white'}`}>
          <Icon name={toast.type === 'success' ? 'CheckCircleIcon' : 'XCircleIcon'} size={16} />
          {toast.msg}
        </div>
      )}
    </AppLayout>
  );
}
